const number = value => Number(value ?? 0);
const money = value => Math.round((value + Number.EPSILON) * 100) / 100;
export const itemFields = ['description','provider_item_code','quantity','unit_price','tax','impuesto_consumo','discount','product_id','total_price'];

export function itemDraft(item = null) {
  if (!item) return { description:'',provider_item_code:'',quantity:1,unit_price:0,tax:0,impuesto_consumo:0,discount:0,product_id:null };
  const qty = number(item.quantity), unit = number(item.unit_price), discount = number(item.discount);
  const total = number(item.total_price ?? qty * unit);
  // Persisted items use net line totals. Older manually added lines included
  // taxes; recognize that exact convention without rewriting unchanged amounts.
  const legacyGross = Math.abs(total - (qty * unit + number(item.tax) + number(item.impuesto_consumo) - discount)) < .005 &&
    Math.abs(total - qty * unit) > .005;
  const base = legacyGross ? qty * unit - discount : total;
  return { description:item.description ?? '',provider_item_code:item.provider_item_code ?? '',quantity:item.quantity ?? 1,
    unit_price:qty > 0 ? Number(((base + discount) / qty).toPrecision(15)) : unit, tax:item.tax ?? 0,
    impuesto_consumo:item.impuesto_consumo ?? 0,discount:item.discount ?? 0,product_id:item.product_id ?? null };
}

export function validateItemDraft(draft) {
  const errors = {};
  if (!String(draft.description).trim()) errors.description = 'Escribe la descripción de la línea.';
  for (const field of ['quantity','unit_price','tax','impuesto_consumo','discount']) {
    if (String(draft[field] ?? '').trim() === '' || !Number.isFinite(Number(draft[field])) || Number(draft[field]) < 0) {
      errors[field] = 'Introduce un número válido mayor o igual a cero.';
    }
  }
  if (number(draft.quantity) <= 0) errors.quantity = 'La cantidad debe ser mayor que cero.';
  if (!errors.discount && number(draft.discount) > number(draft.quantity) * number(draft.unit_price)) {
    errors.discount = 'El descuento no puede superar el valor de la línea.';
  }
  return errors;
}

export function itemPayload(draft, original = null) {
  if (Object.keys(validateItemDraft(draft)).length) throw new Error('Revisa los campos de la línea.');
  const baseline = itemDraft(original);
  const changed = field => ['description','provider_item_code'].includes(field)
    ? String(draft[field]).trim() !== String(baseline[field]).trim()
    : field === 'product_id' ? String(draft[field] ?? '') !== String(baseline[field] ?? '') : number(draft[field]) !== number(baseline[field]);
  const financialChange = ['quantity','unit_price','discount'].some(changed);
  if (original?.original_unit_price != null && financialChange) throw new Error('Restaura los precios originales de esta factura antes de cambiar cantidad, precio o descuento.');
  const payload = {};
  for (const field of Object.keys(baseline)) {
    if (!original || changed(field)) payload[field] = ['description','provider_item_code'].includes(field)
      ? String(draft[field]).trim() : field === 'product_id' ? draft[field] || null : number(draft[field]);
  }
  if (!original || financialChange) {
    const qty = number(draft.quantity);
    const base = money(qty * number(draft.unit_price) - number(draft.discount));
    Object.assign(payload,{quantity:qty,unit_price:Number((base / qty).toPrecision(15)),discount:number(draft.discount),total_price:base});
  }
  if (!original || changed('product_id')) payload.product_match_manual = original ? true : payload.product_id != null;
  return payload;
}

export async function writeInvoiceItem({supabase,userId,invoiceId,original,payload,requireEditingEnabled = Boolean(original)}) {
  if (!userId || !invoiceId) throw new Error('Tu sesión no está disponible. Vuelve a iniciar sesión; tus cambios siguen aquí.');
  if (requireEditingEnabled) {
    const {data,error} = await supabase.from('invoice_users').select('enable_invoice_item_editing').eq('id',userId).single();
    if (error || data?.enable_invoice_item_editing !== true) throw new Error('La edición de líneas está desactivada para este cliente o no se pudo verificar. Revisa Opciones generales antes de guardar.');
  }
  if (original && !Object.keys(payload).length) return original;
  let request;
  if (original) {
    request = supabase.from('invoice_items').update(payload).eq('id',original.id).eq('invoice_id',invoiceId).eq('user_id',userId);
    for (const field of itemFields) {
      if (original[field] === undefined) continue;
      request = original[field] === null ? request.is(field,null) : request.eq(field,original[field]);
    }
    const {data,error} = await request.select().maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('La línea cambió o ya no está disponible. Cierra el editor y vuelve a abrirla para revisar su versión actual.');
    return data;
  }
  const {data,error} = await supabase.from('invoice_items').insert([{...payload,invoice_id:invoiceId,user_id:userId}]).select().single();
  if (error) throw error;
  if (!data) throw new Error('No se pudo confirmar que la línea quedara guardada.');
  return data;
}

export function itemAmountsChanged(before,after) {
  return !before || ['quantity','unit_price','tax','impuesto_consumo','discount','total_price']
    .some(field => number(before[field]) !== number(after[field]));
}
